import { useCallback, useEffect, useState } from 'react';
import { clientFor, useAccounts } from '../auth/store';
import { useT } from '../i18n';
import { MailError, type AdminMailbox } from '../api/types';
import { avatarColor, initials } from '../mail/format';

/*
    Domain management — opening a mailbox and resetting a password.

    Where the work actually happens matters here. Both of these need a privileged database
    credential, and that credential lives in a service on the mail server. This screen, and the
    webmail behind it, hold none of it: what travels is the signed-in person's OWN mailbox
    password, which the service checks against the mail server before doing anything.

    The tab is drawn only when the server has said this mailbox administers a domain. A tab that
    opens onto "you may not do this" is worse than no tab — the same rule the forwarding screen
    follows for a missing Sieve server.

    Two things this screen is careful about:

      - A NEW PASSWORD CANNOT BE READ BACK. The mail server stores a hash and nothing here can
        undo that, so the screen says so BEFORE the save rather than after, when it would only be
        an apology.

      - THE REFUSAL IS THE SERVICE'S OWN SENTENCE. "This domain is not yours" tells somebody what
        to do next; "403" does not.
*/

const card: React.CSSProperties = {
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-card)',
    padding: 18,
    display: 'grid',
    gap: 12,
};

const input: React.CSSProperties = {
    height: 30,
    padding: '0 10px',
    background: 'var(--surface2)',
    border: '1px solid var(--border2)',
    borderRadius: 'var(--r-btn)',
    color: 'var(--text)',
    fontSize: 'var(--fs-sm)',
    minWidth: 0,
};

const ghost: React.CSSProperties = {
    height: 30,
    padding: '0 12px',
    background: 'transparent',
    border: '1px solid var(--border2)',
    borderRadius: 'var(--r-btn)',
    color: 'var(--text-dim)',
    fontSize: 'var(--fs-sm)',
};

export default function AdminSettings() {
    const { t } = useT();
    const account = useAccounts((a) => a.accounts.find((x) => x.id === a.activeId));

    const [kutular, setKutular] = useState<AdminMailbox[] | null>(null);
    const [hata, setHata] = useState<string | null>(null);
    const [bilgi, setBilgi] = useState<string | null>(null);
    const [ortakDurum, setOrtakDurum] = useState<{ path: string; contacts: string; calendar: string } | null>(null);
    const [calisiyor, setCalisiyor] = useState(false);

    const [yeniAdres, setYeniAdres] = useState('');
    const [yeniAd, setYeniAd] = useState('');
    const [yeniParola, setYeniParola] = useState('');

    const [hedef, setHedef] = useState('');
    const [hedefParola, setHedefParola] = useState('');

    /*
        Marka logosu.

        `logoSurumu` bir önizleme hilesi değil, gereklilik: yüklenen logo hep AYNI adresten
        (`/brand/logo`) geliyor, yani tarayıcı yeni dosyayı eskisiyle aynı sanır ve ekranda hiçbir
        şey değişmez. Yükleyen de haklı olarak "olmadı" der ve bir daha yükler. Sayaç adresi
        değiştirerek o döngüyü kesiyor.
    */
    const [logoSurumu, setLogoSurumu] = useState(0);
    const [logoYukleniyor, setLogoYukleniyor] = useState(false);

    const yukle = useCallback(async () => {
        if (!account) return;
        try {
            setKutular(await clientFor(account.email).adminMailboxes());
            setHata(null);
        } catch (e) {
            // The list failing is shown rather than swallowed: an empty list and a list that could
            // not be read look the same, and one of them means something is wrong.
            setHata(e instanceof MailError ? e.message : String(e));
            setKutular([]);
        }
    }, [account]);

    useEffect(() => {
        void yukle();
    }, [yukle]);

    if (!account) return <div style={card}>{t('identity.noAccount')}</div>;
    const client = clientFor(account.email);

    const kos = async (is: () => Promise<void>, sonMesaj: string) => {
        setCalisiyor(true);
        setHata(null);
        setBilgi(null);
        try {
            await is();
            setBilgi(sonMesaj);
            await yukle();
        } catch (e) {
            setHata(e instanceof MailError ? e.message : String(e));
        } finally {
            setCalisiyor(false);
        }
    };

    const kutuAc = () =>
        kos(async () => {
            await client.adminCreateMailbox(yeniAdres.trim(), yeniAd.trim(), yeniParola);
            setYeniAdres('');
            setYeniAd('');
            setYeniParola('');
        }, t('admin.created'));

    // Running this twice is a success, not a failure: the answer says for each collection whether
    // it was made or was already there, and the screen repeats that rather than claiming it made
    // something it found.
    const ortakAc = async () => {
        setCalisiyor(true);
        setHata(null);
        setBilgi(null);
        try {
            const durum = await client.adminOpenShared();
            setOrtakDurum(durum);
            setBilgi(t('admin.sharedDone', { path: durum.path, contacts: durum.contacts, calendar: durum.calendar }));
        } catch (e) {
            setHata(e instanceof MailError ? e.message : String(e));
        } finally {
            setCalisiyor(false);
        }
    };

    const parolaSifirla = () =>
        kos(async () => {
            const { signedOut } = await client.adminResetPassword(hedef, hedefParola);
            setHedefParola('');
            /*
                Kendi parolasını sıfırlayan yönetici oturumdan düşer: oturumun bellekte tuttuğu
                parola artık eskisidir. Sunucu oturumu kendisi kapatıyor; ekranın işi kişiyi
                giriş sayfasına götürmek. Eskiden aynı tıklamada yeşil "parola değişti" ile
                kırmızı bir hata yan yana beliriyordu ve sebebi ekranda yazmıyordu.
            */
            if (signedOut) window.location.assign('/login');
        }, t('admin.resetDone'));

    return (
        <div style={{ display: 'grid', gap: 12 }} data-testid="admin-panel">
            {hata ? (
                <div style={{ ...card, color: 'var(--red)' }} data-testid="admin-error">
                    {hata}
                </div>
            ) : null}
            {bilgi ? (
                <div style={{ ...card, color: 'var(--accent-strong)' }} data-testid="admin-done">
                    {bilgi}
                </div>
            ) : null}

            <div style={card}>
                <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600 }}>{t('admin.title')}</div>
                <div className="kicker" style={{ lineHeight: 1.5 }}>
                    {t('admin.hint')}
                </div>
                {kutular === null ? (
                    <div className="kicker">{t('app.loading')}…</div>
                ) : kutular.length === 0 ? (
                    <div className="kicker">{t('admin.none')}</div>
                ) : (
                    <div style={{ display: 'grid', gap: 4 }} data-testid="admin-mailboxes">
                        {kutular.map((k) => (
                            <div
                                key={k.adres}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    padding: '6px 0',
                                    borderBottom: '1px solid var(--border)',
                                }}
                            >
                                <span
                                    aria-hidden="true"
                                    style={{
                                        width: 26,
                                        height: 26,
                                        flex: '0 0 auto',
                                        borderRadius: 'var(--r-pill)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 'var(--fs-2xs)',
                                        fontWeight: 700,
                                        color: '#fff',
                                        background: avatarColor(k.adres),
                                        // Pasif kutu soluk: listede bir satırın çalışmadığını
                                        // rozet söylüyor, avatar da onu tekrar etmek yerine
                                        // sessizleşiyor.
                                        opacity: k.aktif ? 1 : 0.45,
                                    }}
                                >
                                    {initials(k.ad || k.adres)}
                                </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 'var(--fs-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {k.adres}
                                    </div>
                                    {k.ad ? (
                                        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {k.ad}
                                        </div>
                                    ) : null}
                                </div>
                                {!k.aktif ? (
                                    /* Mono kicker değil rozet: "pasif" bir durum, bir yorum değil.
                                       Yazı --text, çerçeve --amber — dolgunun üstüne kehribar yazı
                                       açık temada AA'yı geçmiyor (2. adımda ölçüldü). */
                                    <span
                                        style={{
                                            flex: '0 0 auto',
                                            fontSize: 'var(--fs-2xs)',
                                            fontWeight: 600,
                                            letterSpacing: '0.4px',
                                            textTransform: 'uppercase',
                                            padding: '1px 6px',
                                            borderRadius: 'var(--r-badge)',
                                            background: 'var(--amber-bg)',
                                            color: 'var(--text)',
                                            border: '1px solid var(--amber)',
                                        }}
                                    >
                                        {t('admin.inactive')}
                                    </span>
                                ) : null}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div style={card}>
                <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600 }}>{t('admin.newTitle')}</div>
                {/* Adres ve görünen ad yan yana: ikisi aynı sorunun iki yarısı. Dar ekranda
                    sarıyorlar, çünkü 680 pikselin altında iki alan yan yana ikisini birden
                    okunmaz yapar. */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input
                        value={yeniAdres}
                        onChange={(e) => setYeniAdres(e.target.value)}
                        placeholder={t('admin.address')}
                        style={{ ...input, flex: '1 1 220px' }}
                        data-testid="admin-new-address"
                    />
                    <input
                        value={yeniAd}
                        onChange={(e) => setYeniAd(e.target.value)}
                        placeholder={t('admin.name')}
                        style={{ ...input, flex: '1 1 160px' }}
                        data-testid="admin-new-name"
                    />
                </div>
                <input
                    value={yeniParola}
                    onChange={(e) => setYeniParola(e.target.value)}
                    placeholder={t('admin.password')}
                    type="password"
                    style={input}
                    data-testid="admin-new-password"
                />
                {/*
                    Uyarı kaydetmeden ÖNCE ve bir bant olarak, kicker olarak değil: yeni parola
                    geri okunamaz, ve bunu kaydettikten sonra söylemek bir özürden ibaret olurdu.
                    Kehribar, çünkü bu bir hata değil — geri alınamaz bir adım.
                */}
                <div
                    style={{
                        padding: '9px 11px',
                        background: 'var(--amber-bg)',
                        border: '1px solid var(--amber)',
                        borderRadius: 'var(--r-btn)',
                        fontSize: 'var(--fs-sm)',
                        color: 'var(--text)',
                        lineHeight: 1.5,
                    }}
                >
                    {t('admin.passwordHint')}
                </div>
                <div>
                    <button
                        type="button"
                        onClick={kutuAc}
                        disabled={calisiyor || !yeniAdres.trim() || yeniParola.length < 10}
                        style={{ ...ghost, opacity: calisiyor || !yeniAdres.trim() || yeniParola.length < 10 ? 0.5 : 1 }}
                        data-testid="admin-create"
                    >
                        {t('admin.create')}
                    </button>
                </div>
            </div>

            <div style={card}>
                <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600 }}>{t('admin.sharedTitle')}</div>
                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)', lineHeight: 1.5 }}>
                    {t('admin.sharedHint')}
                </div>
                {/*
                    Çalıştırıldıysa sonucu tik ve sunucu yoluyla gösteriliyor — ÖNCESİNDE bir şey
                    iddia edilmiyor.

                    Teslim "açık olanlar yeşil tikle listelenir" diyor, ama bunu çalıştırmadan
                    bilmenin bir yolu YOK: durumu soran bir uç yok, ve açan ucu "öğrenmek için"
                    çağırmak koleksiyonu gerçekten açardı. Bilmediği bir şeyi tikle gösteren bir
                    ekran, olmayan bir bilgiyi uydurmuş olurdu.
                */}
                {ortakDurum ? (
                    <div style={{ display: 'grid', gap: 4 }} data-testid="admin-shared-state">
                        {[
                            { ad: t('admin.sharedContacts'), yol: `${ortakDurum.path}kisiler/`, hal: ortakDurum.contacts },
                            { ad: t('admin.sharedCalendar'), yol: `${ortakDurum.path}takvim/`, hal: ortakDurum.calendar },
                        ].map((satir) => (
                            <div key={satir.ad} style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                                <span aria-hidden="true" style={{ color: 'var(--green)', flex: '0 0 auto' }}>✓</span>
                                <span style={{ fontSize: 'var(--fs-sm)' }}>{satir.ad}</span>
                                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', overflowWrap: 'anywhere' }}>
                                    {satir.yol}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : null}
                <div>
                    <button
                        type="button"
                        onClick={ortakAc}
                        disabled={calisiyor}
                        style={{ ...ghost, opacity: calisiyor ? 0.5 : 1 }}
                        data-testid="admin-shared-open"
                    >
                        {ortakDurum ? t('admin.sharedAgain') : t('admin.sharedOpen')}
                    </button>
                </div>
            </div>

            {/*
                Marka logosu.

                Alan `operatorLogo`dur, `logo` DEĞİL: ikincisi ürünün kendi işareti (summail web),
                birincisi bu adresin sahibi olan firmanın. Yüklenen dosyayı `logo`ya yazmak,
                müşterinin adresinin bizim olduğunu iddia etmek olurdu.
            */}
            <div style={card}>
                <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600 }}>{t('admin.logoTitle')}</div>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-dim)', lineHeight: 1.6 }}>
                    {t('admin.logoHint')}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-5)', flexWrap: 'wrap' }}>
                    <img
                        key={logoSurumu}
                        src={`/brand/logo?v=${logoSurumu}`}
                        alt=""
                        onError={(e) => {
                            // Henüz logo yok: kırık bir görsel simgesi, hiç görsel olmamasından kötüdür.
                            (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                        style={{ height: 40, width: 'auto', maxWidth: 160, objectFit: 'contain' }}
                        data-testid="marka-logo-onizleme"
                    />
                    <label
                        style={{ ...ghost, cursor: logoYukleniyor ? 'default' : 'pointer', opacity: logoYukleniyor ? 0.5 : 1 }}
                        data-testid="marka-logo-sec"
                    >
                        {logoYukleniyor ? t('admin.logoUploading') : t('admin.logoChoose')}
                        <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            style={{ display: 'none' }}
                            disabled={logoYukleniyor}
                            onChange={async (e) => {
                                const dosya = e.target.files?.[0];
                                e.target.value = '';
                                if (!dosya || !account) return;
                                setLogoYukleniyor(true);
                                setHata(null);
                                setBilgi(null);
                                try {
                                    await clientFor(account.email).adminSaveLogo(dosya, 'acik');
                                    setLogoSurumu((v) => v + 1);
                                    setBilgi(t('admin.logoDone'));
                                } catch (err) {
                                    setHata(err instanceof Error ? err.message : String(err));
                                } finally {
                                    setLogoYukleniyor(false);
                                }
                            }}
                        />
                    </label>
                    <button
                        type="button"
                        style={{ ...ghost, opacity: logoYukleniyor ? 0.5 : 1 }}
                        disabled={logoYukleniyor}
                        data-testid="marka-logo-kaldir"
                        onClick={async () => {
                            if (!account) return;
                            setLogoYukleniyor(true);
                            setHata(null);
                            setBilgi(null);
                            try {
                                await clientFor(account.email).adminRemoveLogo('acik');
                                setLogoSurumu((v) => v + 1);
                                setBilgi(t('admin.logoRemoved'));
                            } catch (err) {
                                setHata(err instanceof Error ? err.message : String(err));
                            } finally {
                                setLogoYukleniyor(false);
                            }
                        }}
                    >
                        {t('admin.logoRemove')}
                    </button>
                </div>
            </div>

            <div style={card}>
                <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600 }}>{t('admin.resetTitle')}</div>
                <select
                    value={hedef}
                    onChange={(e) => setHedef(e.target.value)}
                    // Named out loud: a select whose only label is its first option is
                    // unlabelled to a screen reader, and axe says so.
                    aria-label={t('admin.resetFor')}
                    style={{ ...input, height: 32 }}
                    data-testid="admin-reset-target"
                >
                    <option value="">{t('admin.resetFor')}</option>
                    {(kutular ?? []).map((k) => (
                        <option key={k.adres} value={k.adres}>
                            {k.adres}
                        </option>
                    ))}
                </select>
                <input
                    value={hedefParola}
                    onChange={(e) => setHedefParola(e.target.value)}
                    placeholder={t('admin.password')}
                    type="password"
                    style={input}
                    data-testid="admin-reset-password"
                />
                <div className="kicker" style={{ lineHeight: 1.5 }}>
                    {t('admin.passwordHint')}
                </div>
                <div>
                    <button
                        type="button"
                        onClick={parolaSifirla}
                        disabled={calisiyor || !hedef || hedefParola.length < 10}
                        /* Kırmızı ÇERÇEVE, dolgu değil: bu geri alınamaz bir işlem, ama sık
                           yapılan bir işlem de — kırmızı dolgu her açılışta bir alarm gibi
                           durur ve alarm sürekli olduğunda okunmaz olur. */
                        style={{
                            ...ghost,
                            border: '1px solid var(--red)',
                            color: 'var(--red)',
                            opacity: calisiyor || !hedef || hedefParola.length < 10 ? 0.5 : 1,
                        }}
                        data-testid="admin-reset"
                    >
                        {t('admin.reset')}
                    </button>
                </div>
            </div>

            {/*
                Yetkinin nereden okunduğu ve izin nereye düştüğü. Bir ekranın en altındaki gri
                bant, "bunu neden yapabiliyorum" sorusunun cevabının durduğu yerdir — ve bu
                ekranda o cevabın kendisi bir güvenlik özelliği: yetki uydurulmuyor, posta
                sunucusundaki kayıttan okunuyor.
            */}
            <div
                style={{
                    padding: '10px 12px',
                    background: 'var(--chip-bg)',
                    borderRadius: 'var(--r-btn)',
                    fontSize: 'var(--fs-xs)',
                    color: 'var(--text-dim)',
                    lineHeight: 1.6,
                }}
            >
                {t('admin.authorityHint')}
            </div>
        </div>
    );
}
